import { useEffect, useState } from 'react';
import useAuth from 'CustomHooks/useAuth';
import { fetchWebsites, WebsiteSummary, websiteApiError } from './api';

export type ManagedWebsitesState =
  | { status: 'loading' }
  | { status: 'none' }
  | { status: 'available'; websites: WebsiteSummary[] }
  | { status: 'forbidden' }
  | { status: 'error' };

// The app entry, its card and the product icon ask the same question at the
// same time: they share one request while it runs, per signed-in user and
// restaurant. No answer is kept beyond that, so an activation by Gastronaut
// (or another sign-in) is seen on the next question, e.g. on returning to the tab.
const inFlight = new Map<string, Promise<ManagedWebsitesState>>();

const load = (key: string, restaurantId: string) => {
  let request = inFlight.get(key);
  if (!request) {
    request = fetchWebsites(restaurantId)
      .then(({ websites }): ManagedWebsitesState => (websites.length ? { status: 'available', websites } : { status: 'none' }))
      .catch((error): ManagedWebsitesState => {
        const { status } = websiteApiError(error);
        return status === 401 || status === 403 ? { status: 'forbidden' } : { status: 'error' };
      })
      .finally(() => inFlight.delete(key));
    inFlight.set(key, request);
  }
  return request;
};

/** Whether this restaurant has a website activated by Gastronaut, as the API sees it. */
const useManagedWebsites = (restaurantId: string | null | undefined, enabled = true): ManagedWebsitesState => {
  const { uid } = useAuth();
  const key = restaurantId && uid ? `${uid}:${restaurantId}` : '';
  const [state, setState] = useState<{ key: string; value: ManagedWebsitesState }>({
    key: '',
    value: { status: 'loading' },
  });

  useEffect(() => {
    if (!key || !restaurantId || !enabled) return;
    let active = true;
    const ask = () => load(key, restaurantId).then((value) => active && setState({ key, value }));
    const onVisible = () => {
      if (document.visibilityState === 'visible') ask();
    };
    ask();
    window.addEventListener('focus', ask);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      active = false;
      window.removeEventListener('focus', ask);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [key, restaurantId, enabled]);

  if (!enabled) return { status: 'forbidden' };
  return state.key === key && key ? state.value : { status: 'loading' };
};

export default useManagedWebsites;
